import { Button, Input, Select } from 'antd';
import { assoc, prop, or, and, map, propOr } from 'ramda';
import { isNilOrEmpty, isNotNilOrEmpty } from 'ramda-adjunct';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ContentBlock } from '../../components';
import SwitchByExisting from '../../components/SwitchByExisting';
import Title from '../../components/Title';
import useKeyPressEnter from '../../hooks/useKeyPressEnter';
import useActionGuard from '../../hooks/useActionGuard';
import { errorMessage, infoMessage, successMessage } from '../../utils/messageMutation';
import CreateOfferFromExistingMutation from '../../_graphql/mutations/offers/CreateOfferFromExistingMutation';
import CreateOfferMutation from '../../_graphql/mutations/offers/CreateOfferMutation';
import { removeNull } from '../helpers/removeNull';
import ModalDuplicateOffer from '../../components/ModalDuplicateOffer';
import withUser from '../../withUser';
import { QOffersQuery } from '../../_graphql/queries/QOffers';
import useFetchQuery from '../../hooks/useFetchQuery';
import Loading from '../../components/Loading';
import useUserRefreshLock from '../../hooks/useUserRefreshLock';

const useCreateOffer = (openStatusModal, onError) => {
  const { lockUserRefresh, unlockUserRefresh } = useUserRefreshLock();
  const [values, setValues] = useState({
    offerName: '',
    fromOfferId: undefined,
    isCopyOffer: false
  });
  const { loading, run } = useActionGuard();

  const setValue = (k, v) => setValues(assoc(k, v));
  const history = useHistory();

  const onCreate = () => {
    run('create-offer', (done) => {
      const isCopyOffer = prop('isCopyOffer', values);
      if (openStatusModal && isCopyOffer) openStatusModal();

      const callback = (ok, error) => {
        if (ok && !error) {
          successMessage('offre', 'créée avec succès', true);
          if (!isCopyOffer) history.push('/');
        } else {
          errorMessage();
          !!onError && onError();
        }
        done(ok, error);
      };

      if (!isCopyOffer) {
        CreateOfferMutation(values, callback);
      } else {
        infoMessage('Offre en cours de création, veuillez patienter...');
        // Lock user refresh: the periodic silent QUserQuery refresh could
        // otherwise re-render mid-duplication and interrupt the operation.
        lockUserRefresh();
        CreateOfferFromExistingMutation(values, (ok, error) => {
          unlockUserRefresh();
          callback(ok, error);
        });
      }
    });
  };

  const buttonIsDisabled = or(
    isNilOrEmpty(prop('offerName', values)),
    and(prop('isCopyOffer', values), isNilOrEmpty(prop('fromOfferId', values)))
  );

  useKeyPressEnter(onCreate, !buttonIsDisabled, values);

  return {
    values,
    loading: propOr(false, 'create-offer', loading),
    onCreate,
    setValue,
    buttonIsDisabled
  };
};

const CreateOffer = ({ user, userTeam }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [errorDuplicateOffer, setErrorDuplicateOffer] = useState(false);

  const openStatusModal = () => setIsOpen(true);
  const onError = () => setErrorDuplicateOffer(true);

  const {
    values,
    loading,
    setValue,
    onCreate,
    buttonIsDisabled
  } = useCreateOffer(openStatusModal, onError);

  const teamId = prop('teamId', userTeam);
  const args = useMemo(() => ({ filter: { team: teamId, hideArchived: true } }), [teamId]);

  const { offers, isLoading: isLoadingOffers } = useFetchQuery({
    query: QOffersQuery,
    args,
    dataProp: 'offers',
    defaultData: []
  });

  if (isLoadingOffers) return <Loading />;

  return (
    <>
      <Title title="Création d'une offre" backTo="/" />
      <ContentBlock>
        <h4 className="font-bold mb-2">{'Nom de l\'offre'}</h4>
        <Input
          placeholder="Nom de l'offre"
          value={prop('offerName', values)}
          onChange={e => setValue('offerName', e.target.value)}
          disabled={loading}
        />
        {isNotNilOrEmpty(removeNull(offers)) && (
          <SwitchByExisting
            checked={prop('isCopyOffer', values)}
            onChange={checked => setValue('isCopyOffer', checked)}
            onClick={() => setValue('isCopyOffer', !prop('isCopyOffer', values))}
            label="Créer à partir d'une offre existante"
            fromValue={prop('fromOfferId', values)}
            onSelect={v => setValue('fromOfferId', v)}
            placeholder="Offre à dupliquer"
            options={map(({ offerName, offerId, version }) => {
              const title = `${offerName} - ${prop('major', version)}.${prop('minor', version)}`;
              return (
                <Select.Option
                  key={offerId}
                  value={offerId}
                  title={title}
                >
                  {title}
                </Select.Option>
              );
            })(removeNull(offers))}
          />
        )}
        <Button
          loading={loading}
          disabled={buttonIsDisabled}
          onClick={onCreate}
          type="primary"
          size="large"
          className="block mt-6"
        >
          Créer la nouvelle offre
        </Button>
      </ContentBlock>
      <ModalDuplicateOffer
        open={isOpen}
        setIsOpen={setIsOpen}
        redirectTo="/"
        error={errorDuplicateOffer}
        userId={prop('userId', user)}
        offerId={prop('fromOfferId', values)}
      />
    </>
  );
};

export default withUser(CreateOffer);
